const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;
    --ctl-color-backdrop: var(--ctl-color-overlay-7);
    --ctl-color-focus: var(--ctl-color-brand);

    --ctl-radius: var(--ctl-radius-lg, 16px);
    --ctl-shadow: var(--ctl-shadow-lg);
    --ctl-max-width: 480px;
    --ctl-pad: var(--spacing-md, 16px);

    display: contents;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .layer{
    position: fixed;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-lg, 24px);
    z-index: 2000;
  }

  :host([open]) .layer{
    display: flex;
  }

  .backdrop{
    position: absolute;
    inset: 0;
    background: var(--ctl-color-backdrop);
  }

  .dialog{
    position: relative;
    width: min(var(--ctl-max-width), calc(100vw - var(--spacing-xxl, 48px)));
    max-height: calc(100vh - var(--spacing-xxl, 48px));
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header{
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-sm, 12px);
    padding: var(--ctl-pad);
    border-bottom: 1px solid var(--ctl-color-border);
  }

  .icon{
    flex: 0 0 auto;
    width: var(--spacing-xxl, 48px);
    height: var(--spacing-xxl, 48px);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-xl, 1.5rem);
  }

  /* Variant: info (default) */
  :host(:not([variant])) .icon,
  :host([variant="info"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-info, #2563eb) 10%, transparent);
    color: var(--ctl-color-info, #2563eb);
  }

  /* Variant: success */
  :host([variant="success"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-success, #059669) 10%, transparent);
    color: var(--ctl-color-success, #059669);
  }

  /* Variant: warning */
  :host([variant="warning"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-warning, #d97706) 10%, transparent);
    color: var(--ctl-color-warning, #d97706);
  }

  /* Variant: danger */
  :host([variant="danger"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-danger, #dc2626) 10%, transparent);
    color: var(--ctl-color-danger, #dc2626);
  }

  .title-group{
    flex: 1;
    min-width: 0;
  }

  .title{
    margin: 0;
    font-size: var(--font-size-md, 1rem);
    font-weight: var(--font-weight-semibold, 600);
  }

  .title ::slotted(*){
    margin: 0;
  }

  .body{
    padding: 0 var(--ctl-pad) var(--ctl-pad) var(--ctl-pad);
    color: var(--ctl-color-muted);
    font-size: var(--font-size-sm, 0.875rem);
    line-height: var(--line-height-normal, 1.5);
  }

  .footer{
    padding: var(--ctl-pad);
    border-top: 1px solid var(--ctl-color-border);
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-xs, 8px);
  }

  .footer:empty{
    display: none;
  }

  .btn{
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xs, 8px) var(--spacing-md, 16px);
    border-radius: var(--border-radius-md, 8px);
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--font-weight-semibold, 600);
    cursor: pointer;
    transition: all var(--transition-fast, 150ms ease-in-out);
  }

  .btn:focus-visible{
    outline: 2px solid var(--ctl-color-focus);
    outline-offset: 2px;
  }

  .btn-cancel{
    background: transparent;
    color: var(--ctl-color-text);
    border: 1px solid var(--ctl-color-border);
  }

  .btn-cancel:hover{
    background: color-mix(in srgb, var(--color-black) 4%, transparent);
  }

  .btn-confirm{
    background: var(--ctl-color-focus);
    color: white;
  }

  .btn-confirm:hover{
    background: color-mix(in srgb, var(--ctl-color-focus) 85%, black);
  }

  /* Fallback for browsers without color-mix support */
  @supports not (background: color-mix(in srgb, red, blue)) {
    .btn-confirm:hover{
      filter: brightness(0.9);
    }
  }

  /* Danger variant confirm button */
  :host([variant="danger"]) .btn-confirm{
    background: var(--ctl-color-danger);
  }

  :host([variant="danger"]) .btn-confirm:hover{
    background: color-mix(in srgb, var(--ctl-color-danger) 85%, black);
  }

  /* Fallback for browsers without color-mix support */
  @supports not (background: color-mix(in srgb, red, blue)) {
    :host([variant="danger"]) .btn-confirm:hover{
      filter: brightness(0.9);
    }
  }
</style>

<div class="layer" part="layer">
  <div class="backdrop" part="backdrop"></div>

  <div
    class="dialog"
    part="dialog"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="ctlConfirmTitle"
    aria-describedby="ctlConfirmBody"
    tabindex="-1"
  >
    <div class="header" part="header">
      <div class="icon" part="icon" id="icon">
        <slot name="icon">ℹ️</slot>
      </div>

      <div class="title-group">
        <div class="title" id="ctlConfirmTitle" part="title">
          <slot name="title"></slot>
        </div>
      </div>
    </div>

    <div class="body" id="ctlConfirmBody" part="body">
      <slot></slot>
    </div>

    <div class="footer" part="footer">
      <button class="btn btn-cancel" id="cancelBtn" type="button" part="cancel-button">
        <slot name="cancel-text">Cancel</slot>
      </button>
      <button class="btn btn-confirm" id="confirmBtn" type="button" part="confirm-button">
        <slot name="confirm-text">Confirm</slot>
      </button>
    </div>
  </div>
</div>
`;

function getFocusable(root) {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ];
  return Array.from(root.querySelectorAll(selectors.join(",")))
    .filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
}

export class CtlConfirmDialog extends HTMLElement {
  static get observedAttributes() {
    return ["open", "variant", "aria-label", "aria-labelledby", "aria-describedby"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._layer = this.shadowRoot.querySelector(".layer");
    this._backdrop = this.shadowRoot.querySelector(".backdrop");
    this._dialog = this.shadowRoot.querySelector(".dialog");
    this._cancelBtn = this.shadowRoot.getElementById("cancelBtn");
    this._confirmBtn = this.shadowRoot.getElementById("confirmBtn");

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onBackdropDown = this._onBackdropDown.bind(this);

    this._lastActive = null;
  }

  connectedCallback() {
    this._syncAria();

    this._cancelBtn.addEventListener("click", () => this.close("cancel"));
    this._confirmBtn.addEventListener("click", () => this.close("confirm"));
    this._backdrop.addEventListener("pointerdown", this._onBackdropDown);
  }

  disconnectedCallback() {
    this._backdrop.removeEventListener("pointerdown", this._onBackdropDown);
    document.removeEventListener("keydown", this._onKeyDown, true);
  }

  attributeChangedCallback() {
    this._syncAria();
    this._syncOpen();
  }

  // --- Public API ---
  show() {
    if (this.hasAttribute("open")) return;
    this._lastActive = document.activeElement;
    this.setAttribute("open", "");
  }

  close(reason = "programmatic") {
    if (!this.hasAttribute("open")) return;
    this._closeReason = reason;
    this.removeAttribute("open");
  }

  toggle() {
    this.hasAttribute("open") ? this.close("toggle") : this.show();
  }

  // --- Internal ---
  _syncAria() {
    const label = this.getAttribute("aria-label");
    const labelledby = this.getAttribute("aria-labelledby");
    const describedby = this.getAttribute("aria-describedby");

    if (label) {
      this._dialog.setAttribute("aria-label", label);
      this._dialog.removeAttribute("aria-labelledby");
    } else {
      this._dialog.removeAttribute("aria-label");
      if (labelledby) this._dialog.setAttribute("aria-labelledby", labelledby);
      else this._dialog.setAttribute("aria-labelledby", "ctlConfirmTitle");
    }

    if (describedby) this._dialog.setAttribute("aria-describedby", describedby);
    else this._dialog.setAttribute("aria-describedby", "ctlConfirmBody");
  }

  _syncOpen() {
    const isOpen = this.hasAttribute("open");

    if (isOpen) {
      document.addEventListener("keydown", this._onKeyDown, true);

      // Focus confirm button by default (or cancel for danger variants)
      queueMicrotask(() => {
        const isDanger = this.getAttribute("variant") === "danger";
        const focusTarget = isDanger ? this._cancelBtn : this._confirmBtn;
        focusTarget.focus({ preventScroll: true });
      });

      this.dispatchEvent(
        new CustomEvent("ctl-confirm-open", { bubbles: true, composed: true, detail: {} })
      );
    } else {
      document.removeEventListener("keydown", this._onKeyDown, true);

      queueMicrotask(() => {
        if (this._lastActive && typeof this._lastActive.focus === "function") {
          this._lastActive.focus({ preventScroll: true });
        }
        this._lastActive = null;
      });

      const reason = this._closeReason || "programmatic";

      this.dispatchEvent(
        new CustomEvent("ctl-confirm-close", {
          bubbles: true,
          composed: true,
          detail: { reason, status: reason }
        })
      );

      // Unified events matching ctl-confirmation API (provide both `reason` and `status`)
      if (reason === "confirm") {
        this.dispatchEvent(
          new CustomEvent("ctl-confirm", {
            bubbles: true,
            composed: true,
            detail: { reason, status: reason }
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent("ctl-cancel", {
            bubbles: true,
            composed: true,
            detail: { reason, status: reason }
          })
        );
      }

      this._closeReason = null;
    }
  }

  _onBackdropDown(e) {
    if (this.hasAttribute("no-backdrop-close")) return;

    const path = e.composedPath ? e.composedPath() : [];
    if (path.includes(this._backdrop)) {
      this.close("backdrop");
    }
  }

  _onKeyDown(e) {
    if (!this.hasAttribute("open")) return;

    if (e.key === "Escape" && !this.hasAttribute("no-esc-close")) {
      e.preventDefault();
      e.stopPropagation();
      this.close("escape");
      return;
    }

    if (e.key !== "Tab") return;

    // Focus trap
    const focusables = getFocusable(this._dialog);
    if (!focusables.length) {
      e.preventDefault();
      this._dialog.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.shadowRoot.activeElement || document.activeElement;

    if (e.shiftKey) {
      if (active === first || active === this._dialog) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

customElements.define("ctl-confirm-dialog", CtlConfirmDialog);
